import type { ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
}

/**
 * Zentrale Button-Komponente im Editorial-Stil (uppercase, tracking-widest).
 *
 * Destilliert aus den bis dahin kopierten Tailwind-Strings in Login, Cockpit,
 * CaseHub etc. — neue Buttons bitte hierüber, nicht per Klassen-Copy-Paste.
 * `type` wird bewusst NICHT defaulted: außerhalb von Formularen explizit
 * `type="button"` setzen (Browser-Default ist `submit`).
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-sm font-sans font-medium uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-stone-900 text-white hover:bg-stone-800 disabled:bg-stone-300',
  secondary:
    'border border-stone-900 bg-white text-stone-900 hover:bg-stone-900 hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-stone-900',
  danger:
    'bg-rose-700 text-white hover:bg-rose-800 focus-visible:ring-rose-700 disabled:bg-rose-300',
  ghost: 'text-stone-500 underline hover:text-stone-900 hover:no-underline disabled:opacity-40',
};

// min-h-[44px] sichert das WCAG-2.5.5-Touch-Target (Hauptgerät Smartphone).
const SIZES: Record<ButtonSize, string> = {
  md: 'min-h-[44px] px-6 py-3 text-xs',
  sm: 'min-h-[44px] px-4 py-2 text-xs',
};

export function Button({ variant = 'primary', size = 'md', className, ...rest }: ButtonProps) {
  const classes = [BASE, VARIANTS[variant], SIZES[size], className].filter(Boolean).join(' ');
  return <button className={classes} {...rest} />;
}
